import CommonAPI from '@fusioncharts/charts/src/chart/_internal/commonchartapi';
import SankeyDS from '../../dataset/sankey';
import sankeyDatasetFactory from '../../factories/sankey-dataset-factory';
import isArray from '@fusioncharts/utils/src/type/is-array';
import { preDefStr, pluck, parseUnsafeString, POSITION_LEFT, POSITION_RIGHT, POSITION_TOP } from '@fusioncharts/core/src/lib';
import legendFactory from '@fusioncharts/charts/src/factories/legend';
import { traverseGraph, createNodeLinks, createNodeValues } from '../../dataset/sankey/sankeyhelper';

const DEFAULT_NODE_WIDTH = 20,
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  RIGHT = 'right',
  INVALIDGRAPH = 'Self loops and circular links are not supported.',
  prepareData = (dataSource) => {
    let parsedNodes = [],
      parsedLinks = [],
      nodes = dataSource.nodes || [],
      links = dataSource.links || [],
      nodeId,
      nodeIds = {};

    nodes.forEach(node => {
      nodeId = pluck(node.id, node.label);
      if (!nodeId) return;

      nodeIds[nodeId.toString()] = node;
    });

    Object.keys(nodeIds).forEach((id, index) => {
      parsedNodes.push(Object.assign({}, nodeIds[id], { props: {} }));
      parsedNodes[index].id = parseUnsafeString((parsedNodes[index].id || parsedNodes[index].label).toString());
      parsedNodes[index].label = parseUnsafeString((parsedNodes[index].label || parsedNodes[index].id).toString());
    });

    links.forEach(link => {
      if (!nodeIds[link.from] || !nodeIds[link.to]) return;

      parsedLinks.push(Object.assign({}, link, {
        props: {
          source: parseUnsafeString(link.from),
          target: parseUnsafeString(link.to)
        },
        value: pluck(link.value, '0')
      }));
    });

    return {
      nodes: parsedNodes,
      links: parsedLinks
    };
  };
class Sankey extends CommonAPI {
  constructor () {
    super();

    this.registerFactory('legend', legendFactory);
    this.registerFactory('dataset', sankeyDatasetFactory, ['legend']);
  }

  __setDefaultConfig () {
    super.__setDefaultConfig();
    let chartConfig = this.config;

    this.legendposition = RIGHT;
    chartConfig.legendPosition = preDefStr.POSITION_BOTTOM;
    chartConfig.orientation = HORIZONTAL;
    chartConfig.skipCanvasDrawing = true;
    chartConfig.alignCaptionWithCanvas = false;
    chartConfig.basefontsize = 11;
  }

  static getName () {
    return 'sankey';
  }
  getName () {
    return 'sankey';
  }

  getDSdef () {
    return SankeyDS;
  }

  _checkInvalidData () {
    let dataSource = this.getFromEnv('dataSource');

    if (!isArray(dataSource.nodes) || !dataSource.nodes.length) {
      delete this.config.errorMessage;
      return true;
    }
    return false;
  }

  // eslint-disable-next-line
  _checkInvalidSpecificData () {
    let chart = this,
      parsedData = prepareData(chart.getFromEnv('dataSource'));

    createNodeLinks(parsedData);
    if (!traverseGraph(parsedData)) {
      chart.config.errorMessage = INVALIDGRAPH;
      return true;
    }

    delete chart.config.errorMessage;
    chart.addToEnv('sankey-graph', parsedData);
    return false;
  }

  setChartMessage (message, chartObj, _container) {
    let { errorMessage } = this.config;

    super.setChartMessage(message || errorMessage, chartObj, _container);
  }

  configureAttributes (inputConfig = {}) {
    super.configureAttributes(inputConfig);

    let chart = this,
      chartConfig = chart.config,
      chartAttr = inputConfig.chart || {},
      numberFormatter = chart.getFromEnv('number-formatter'),
      graph = chart.getFromEnv('sankey-graph'),
      { nodes, links } = graph;

    if (typeof chartAttr.orientation !== 'undefined') {
      chartConfig.orientation = chartAttr.orientation.toString().toLowerCase();
      chartConfig.orientation = chartConfig.orientation === VERTICAL ? VERTICAL : HORIZONTAL;
    } else {
      chartConfig.orientation = HORIZONTAL;
    }

    if (typeof chartAttr.legendposition !== 'undefined') {
      chartConfig.legendPosition = (['top-left', 'top', 'top-right', 'right-top', 'right', 'right-bottom', 'bottom-right', 'bottom', 'bottom-left', 'left-bottom', 'left', 'left-top'].includes(chartAttr.legendposition.toString().toLowerCase()) ? chartAttr.legendposition.toString().toLowerCase() : preDefStr.POSITION_BOTTOM);
    } else {
      chartConfig.legendPosition = RIGHT;
    }
    chartConfig.legendposition = chartConfig.legendPosition;
    chart.addToEnv('orientation', chartConfig.orientation);

    chartConfig.nodeWidth = Math.max(0, numberFormatter.getCleanValue(pluck(chartAttr.nodewidth, DEFAULT_NODE_WIDTH)));
    nodes.forEach(node => {
      node.nodeWidth = chartConfig.nodeWidth;
    });
    links.forEach(link => {
      link.value = Math.max(0, numberFormatter.getCleanValue(pluck(link.value, 0)));
    });

    createNodeValues(graph);
  }

  _spaceManager () {
    let chart = this,
      chartConfig = chart.config,
      {
        legendPosition,
        showLegend,
        marginLeft,
        marginTop,
        marginRight,
        marginBottom
      } = chartConfig,
      width = +chart.getFromEnv('chartWidth'),
      height = +chart.getFromEnv('chartHeight'),
      legend,
      allotedSpace,
      actionBarSpace,
      legendSpace;

    width -= (marginLeft + marginRight);
    height -= (marginTop + marginBottom);

    actionBarSpace = chart._manageActionBarSpace(0.225 * height);
    chart._allocateSpace(actionBarSpace);

    if (showLegend) {
      legendPosition = legendPosition || [];
      if (legendPosition.split('-')[0] === POSITION_TOP || legendPosition.split('-')[0] === preDefStr.POSITION_BOTTOM) {
        allotedSpace = height * 0.3;
      } else {
        allotedSpace = width * 0.3;
        if (legendPosition.split('-')[0] === POSITION_LEFT) {
          chart._allocateSpace({
            left: 11
          });
        } else if (legendPosition.split('-')[0] === POSITION_RIGHT) {
          chart._allocateSpace({
            right: 11
          });
        }
      }

      legend = chart.getChildren('legend')[0];
      legendSpace = legend._manageLegendPosition(allotedSpace);
      chart._allocateSpace(legendSpace);
    }

    chart._manageChartMenuBar(0.6 * chartConfig.availableHeight);
  }

  _postSpaceManagement () {
    let chart = this,
      {
        showLegend
      } = chart.config,
      legends = chart.getChildren('legend');

    if (legends.length && showLegend) {
      legends.forEach(legend => legend.postSpaceManager());
    }
    chart.allocateDimensionOfChartMenuBar();
  }
}

export default Sankey;
